import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Send,
  TrendingUp,
  Warning,
  CheckCircle,
  Info,
  Lightbulb,
  Download,
  Refresh,
  Business,
  Analytics
} from '@mui/icons-material';
import { ResponseFormatter, FormattedResponse, DataVisualization } from '../services/responseFormatter';

// Enhanced Chat Message Interface
interface EnhancedChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  text: string;
  timestamp: Date;
  formattedResponse?: FormattedResponse;
  isProcessing?: boolean;
  error?: string;
}

export function EnhancedBusinessChat() {
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage: EnhancedChatMessage = {
      id: 'welcome',
      type: 'assistant',
      text: 'Hello! I\'m your SleekShop Business Intelligence Assistant. I can help you analyze revenue, orders, inventory, and channel performance. What would you like to know about your business today?',
      timestamp: new Date(),
      formattedResponse: {
        summary: 'Welcome to your business intelligence dashboard!',
        insights: [{
          type: 'summary',
          title: 'Ready to Help',
          description: 'I can analyze any aspect of your business data and provide actionable insights.',
          actionable: false
        }],
        visualizations: [],
        followUpQuestions: [
          'Show me today\'s revenue',
          'What products are running low on stock?',
          'Which sales channels are performing best?',
          'Are there any orders that need attention?'
        ],
        businessContext: 'Your warehouse operates 6:00 AM - 2:30 PM PST with 1:00 PM cutoff for same-day shipping.'
      }
    };
    setMessages([welcomeMessage]);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: EnhancedChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    // Add processing message
    const processingMessage: EnhancedChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      text: 'Analyzing your business data...',
      timestamp: new Date(),
      isProcessing: true
    };

    setMessages(prev => [...prev, processingMessage]);

    try {
      // Simulate API call to backend
      const response = await simulateBackendCall(input.trim());
      
      // Format the response using our ResponseFormatter
      const formattedResponse = ResponseFormatter.formatResponse(input.trim(), response);
      
      // Update processing message with real response
      const assistantMessage: EnhancedChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: formattedResponse.summary,
        timestamp: new Date(),
        formattedResponse
      };

      setMessages(prev => prev.map(msg => 
        msg.isProcessing ? assistantMessage : msg
      ));



    } catch (error) {
      const errorMessage: EnhancedChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: 'I encountered an error while processing your request. Please try again.',
        timestamp: new Date(),
        error: error instanceof Error ? error.message : 'Unknown error'
      };

      setMessages(prev => prev.map(msg => 
        msg.isProcessing ? errorMessage : msg
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  // Simulate backend API call with mock data
  const simulateBackendCall = async (query: string): Promise<any> => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    const queryLower = query.toLowerCase();
    
    // Mock responses based on query content
    if (queryLower.includes('revenue') || queryLower.includes('sales')) {
      return {
        rowsReturned: 3,
        data: [
          { OrderDate: '2025-01-20', FinalGrandTotal: 1250.50, OrderNumber: '3609707' },
          { OrderDate: '2025-01-20', FinalGrandTotal: 890.25, OrderNumber: '3609708' },
          { OrderDate: '2025-01-20', FinalGrandTotal: 2100.75, OrderNumber: '3609709' }
        ]
      };
    }
    
    if (queryLower.includes('order') || queryLower.includes('ship')) {
      return {
        rowsReturned: 2,
        data: [
          { OrderNumber: '3609707', OrderDate: '2025-01-18', ShipmentStatus: 'Not Shipped', CartID: 24 },
          { OrderNumber: '3609708', OrderDate: '2025-01-17', ShipmentStatus: 'Stuck in Packing', CartID: 2 }
        ]
      };
    }
    
    if (queryLower.includes('inventory') || queryLower.includes('stock')) {
      return {
        rowsReturned: 4,
        data: [
          { SKU: 'NP-001', Product: 'Nail Polish Red', QOH: 0, ReorderPoint: 50, Cost: 8.50 },
          { SKU: 'HD-020', Product: 'Hair Developer 20V', QOH: 15, ReorderPoint: 25, Cost: 12.75 },
          { SKU: 'NP-002', Product: 'Nail Polish Blue', QOH: 5, ReorderPoint: 30, Cost: 8.50 },
          { SKU: 'SH-001', Product: 'Shampoo Pro', QOH: 45, ReorderPoint: 20, Cost: 15.00 }
        ]
      };
    }
    
    if (queryLower.includes('channel') || queryLower.includes('marketplace')) {
      return {
        rowsReturned: 4,
        data: [
          { CartID: 24, Channel: 'Target', FinalGrandTotal: 4500.00, Orders: 45 },
          { CartID: 2, Channel: 'Amazon USA', FinalGrandTotal: 3800.00, Orders: 38 },
          { CartID: 11, Channel: 'Direct', FinalGrandTotal: 2200.00, Orders: 22 },
          { CartID: 10, Channel: 'Walmart', FinalGrandTotal: 1800.00, Orders: 18 }
        ]
      };
    }
    
    // Default response
    return {
      rowsReturned: 1,
      data: [
        { Message: 'Query processed successfully', Timestamp: new Date().toISOString() }
      ]
    };
  };

  const handleFollowUpQuestion = (question: string) => {
    setInput(question);
    // Auto-send after a brief delay
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleExportData = (data: any[]) => {
    // Implementation for Excel export
    console.log('Exporting data:', data);
    // You can integrate with your existing export functionality
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return <TrendingUp color="success" />;
      case 'alert': return <Warning color="warning" />;
      case 'recommendation': return <Lightbulb color="info" />;
      default: return <Info color="primary" />;
    }
  };

  const getInsightColor = (severity?: string) => {
    switch (severity) {
      case 'critical': return '#f44336';
      case 'high': return '#ff9800';
      case 'medium': return '#ffc107';
      case 'low': return '#4caf50';
      default: return '#2196f3';
    }
  };

  const renderVisualization = (viz: DataVisualization) => {
    switch (viz.type) {
      case 'metric':
        return (
          <Card sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {viz.data.format === 'currency' ? `$${viz.data.value.toLocaleString()}` : 
               viz.data.format === 'percentage' ? `${((viz.data.value / viz.data.total) * 100).toFixed(1)}%` :
               viz.data.value.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viz.title}
            </Typography>
          </Card>
        );
      
      case 'chart':
        return (
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {viz.title}
            </Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Chart visualization for {viz.config?.type || 'data'} would render here
              </Typography>
            </Box>
          </Card>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business color="primary" />
            <Box>
              <Typography variant="h6">Business Intelligence Chat</Typography>
              <Typography variant="body2" color="text.secondary">
                Get instant insights about your business performance
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton size="small">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Analytics Dashboard">
                <IconButton size="small">
                  <Analytics />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Chat Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
        {messages.map((message) => (
          <Box key={message.id} sx={{ mb: 2 }}>
            {message.type === 'user' ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Paper sx={{ p: 2, maxWidth: '70%', bgcolor: 'primary.main', color: 'white' }}>
                  <Typography>{message.text}</Typography>
                </Paper>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Paper sx={{ p: 2, maxWidth: '80%', bgcolor: 'grey.50' }}>
                  {message.isProcessing ? (
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {message.text}
                      </Typography>
                      <LinearProgress />
                    </Box>
                  ) : message.error ? (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {message.text}
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {message.text}
                      </Typography>
                      
                      {/* Formatted Response */}
                      {message.formattedResponse && (
                        <Box>
                          {/* Business Context */}
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              {message.formattedResponse.businessContext}
                            </Typography>
                          </Alert>
                          
                          {/* Insights */}
                          {message.formattedResponse.insights.length > 0 && (
                            <Accordion defaultExpanded sx={{ mb: 2 }}>
                              <AccordionSummary>
                                <Typography variant="h6">
                                  Business Insights ({message.formattedResponse.insights.length})
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  {message.formattedResponse.insights.map((insight, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                      <Card sx={{ 
                                        p: 2, 
                                        borderLeft: `4px solid ${getInsightColor(insight.severity)}` 
                                      }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                          {getInsightIcon(insight.type)}
                                          <Typography variant="subtitle2" fontWeight="bold">
                                            {insight.title}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          {insight.description}
                                        </Typography>
                                        {insight.actionable && insight.actionText && (
                                          <Button 
                                            size="small" 
                                            variant="outlined" 
                                            startIcon={<CheckCircle />}
                                          >
                                            {insight.actionText}
                                          </Button>
                                        )}
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          )}
                          
                          {/* Visualizations */}
                          {message.formattedResponse.visualizations.length > 0 && (
                            <Accordion defaultExpanded sx={{ mb: 2 }}>
                              <AccordionSummary>
                                <Typography variant="h6">
                                  Data Visualizations ({message.formattedResponse.visualizations.length})
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  {message.formattedResponse.visualizations.map((viz, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                      {renderVisualization(viz)}
                                    </Grid>
                                  ))}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          )}
                          
                          {/* Follow-up Questions */}
                          {message.formattedResponse.followUpQuestions.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Suggested Follow-up Questions:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {message.formattedResponse.followUpQuestions.map((question, index) => (
                                  <Chip
                                    key={index}
                                    label={question}
                                    onClick={() => handleFollowUpQuestion(question)}
                                    variant="outlined"
                                    clickable
                                    size="small"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          
                          {/* Data Export */}
                          {message.formattedResponse.exportData && message.formattedResponse.exportData.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                {message.formattedResponse.exportData.length} records available
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<Download />}
                                onClick={() => handleExportData(message.formattedResponse!.exportData!)}
                              >
                                Export Data
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your business performance..."
              variant="outlined"
              size="small"
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              disabled={isProcessing}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!input.trim() || isProcessing}
              startIcon={<Send />}
            >
              {isProcessing ? 'Processing...' : 'Send'}
            </Button>
          </Box>
          
          {/* Quick Actions */}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Today's Revenue"
              onClick={() => handleFollowUpQuestion("Show me today's revenue")}
              variant="outlined"
              clickable
              size="small"
            />
            <Chip
              label="Low Stock Alert"
              onClick={() => handleFollowUpQuestion("What products are running low on stock?")}
              variant="outlined"
              clickable
              size="small"
            />
            <Chip
              label="Channel Performance"
              onClick={() => handleFollowUpQuestion("Which sales channels are performing best?")}
              variant="outlined"
              clickable
              size="small"
            />
            <Chip
              label="Problem Orders"
              onClick={() => handleFollowUpQuestion("Are there any orders that need attention?")}
              variant="outlined"
              clickable
              size="small"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
